import { useState, useEffect, useMemo, useRef } from 'react';
import { useTranslation } from 'react-i18next';
// Session-free client: a stale login token in localStorage must never
// poison public reads (the careers/survey "empty page" bug class).
import { publicSupabase as supabase } from '../lib/supabase';
import gsap from 'gsap';
import { DataState, SkeletonGrid } from '../components/ui';
import { getLocalizedField, formatPrice, calculateDistance } from '../lib/format';
import { useReveal } from '../hooks/useReveal';

const MENU_GRID = 'grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6';

// Menu Item Card Component
function MenuItemCard({ item, lang, hasBranchSelected, index = 0 }) {
  const { t } = useTranslation();
  const revealRef = useReveal({ index });
  const name = getLocalizedField(item, 'name', lang);
  const description = getLocalizedField(item, 'description', lang);
  const hasPrice = item.price_display || item.price;

  return (
    <div ref={revealRef} className="bg-white rounded-xl shadow-sm overflow-hidden hover:shadow-lg transition-all duration-300 group">
      {/* Image */}
      <div className="aspect-[4/3] bg-gray-100 relative overflow-hidden">
        {item.image_url ? (
          <img
            src={item.image_url}
            alt={name}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
            loading="lazy"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-gray-300">
            <svg className="w-16 h-16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
        )}

        {/* Badges */}
        <div className="absolute top-2 left-2 flex flex-wrap gap-1">
          {item.is_popular && (
            <span className="px-2 py-0.5 bg-yellow-500 text-white text-xs font-medium rounded-full">
              {t('menu.popular')}
            </span>
          )}
          {item.is_new && (
            <span className="px-2 py-0.5 bg-green-500 text-white text-xs font-medium rounded-full">
              {t('menu.new')}
            </span>
          )}
        </div>

        {/* Dietary badges */}
        <div className="absolute top-2 right-2 flex gap-1">
          {item.is_spicy && (
            <span className="w-6 h-6 bg-red-500 text-white text-xs rounded-full flex items-center justify-center" title="Spicy">
              🌶️
            </span>
          )}
          {item.is_vegetarian && (
            <span className="w-6 h-6 bg-green-600 text-white text-xs rounded-full flex items-center justify-center" title="Vegetarian">
              🌱
            </span>
          )}
          {item.is_halal && (
            <span className="w-6 h-6 bg-emerald-600 text-white text-xs rounded-full flex items-center justify-center" title="Halal">
              ✓
            </span>
          )}
        </div>
      </div>

      {/* Content */}
      <div className="p-4">
        <h3 className="font-semibold text-gray-900 text-lg mb-1">{name}</h3>
        {description && (
          <p className="text-sm text-gray-600 line-clamp-2 mb-3">{description}</p>
        )}

        {/* Price */}
        <div className="flex items-center justify-between">
          {hasPrice ? (
            <span className="text-primary-600 font-bold text-lg">
              {item.price_display || formatPrice(item.price, item.currency)}
            </span>
          ) : (
            <span className="text-gray-400 text-sm italic">
              {hasBranchSelected ? t('menu.priceNotSet') : t('menu.selectBranchForPrice')}
            </span>
          )}

          {/* Spice level indicator */}
          {item.spice_level > 0 && (
            <div className="flex items-center gap-0.5" title={`Spice level: ${item.spice_level}/3`}>
              {[...Array(3)].map((_, i) => (
                <span
                  key={i}
                  className={`text-sm ${i < item.spice_level ? 'text-red-500' : 'text-gray-300'}`}
                >
                  🌶️
                </span>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// Branch selector component
function BranchSelector({ branches, selectedBranch, onSelect, userLocation, t }) {
  return (
    <div className="bg-white rounded-xl shadow-sm p-4 mb-6">
      <div className="flex flex-col sm:flex-row sm:items-center gap-4">
        <div className="flex items-center gap-2">
          <svg className="w-5 h-5 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
          </svg>
          <span className="font-medium text-gray-700">{t('menu.selectBranch')}:</span>
        </div>

        <select
          value={selectedBranch?.id || ''}
          onChange={(e) => {
            const branch = branches.find(b => b.id === e.target.value);
            onSelect(branch || null);
          }}
          className="flex-1 sm:flex-none sm:w-64 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
        >
          <option value="">{t('menu.nearestBranch')}</option>
          {branches.map((branch) => (
            <option key={branch.id} value={branch.id}>
              {branch.name} - {branch.city}
              {branch.distance_km && ` (${branch.distance_km.toFixed(1)} km)`}
            </option>
          ))}
        </select>

        {selectedBranch && (
          <div className="text-sm text-gray-500">
            {t('menu.pricesFor')} <span className="font-medium text-gray-700">{selectedBranch.name}</span>
          </div>
        )}
      </div>
    </div>
  );
}

export default function Menu() {
  const { t, i18n } = useTranslation();
  const lang = i18n.language;

  const [menuItems, setMenuItems] = useState([]);
  const [categories, setCategories] = useState([]);
  const [branches, setBranches] = useState([]);
  const [selectedBranch, setSelectedBranch] = useState(null);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userLocation, setUserLocation] = useState(null);
  const [locationError, setLocationError] = useState(null);
  const headerRef = useRef(null);
  const filterRef = useRef(null);
  const branchSelectorRef = useRef(null);

  // Animate header on mount
  useEffect(() => {
    if (!loading && headerRef.current) {
      gsap.fromTo(
        headerRef.current.children,
        { opacity: 0, y: 30 },
        { opacity: 1, y: 0, duration: 0.8, stagger: 0.15, ease: 'power3.out' }
      );
    }
  }, [loading]);

  // Animate branch selector
  useEffect(() => {
    if (!loading && branchSelectorRef.current) {
      gsap.fromTo(
        branchSelectorRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, delay: 0.3, ease: 'power3.out' }
      );
    }
  }, [loading]);

  // Animate category filters
  useEffect(() => {
    if (!loading && filterRef.current) {
      gsap.fromTo(
        filterRef.current.children,
        { opacity: 0, y: 15 },
        { opacity: 1, y: 0, duration: 0.5, stagger: 0.05, delay: 0.4, ease: 'power3.out' }
      );
    }
  }, [loading]);

  // Get user's location on mount
  useEffect(() => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          setUserLocation({
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          });
        },
        (error) => {
          console.log('Geolocation error:', error.message);
          setLocationError(error.message);
        },
        { timeout: 10000, maximumAge: 60000 }
      );
    }
  }, []);

  // Fetch branches
  useEffect(() => {
    fetchBranches();
  }, [userLocation]);

  // Fetch menu items when branch changes
  useEffect(() => {
    fetchMenuItems();
  }, [selectedBranch, userLocation]);

  const fetchBranches = async () => {
    try {
      let query = supabase
        .from('website_branches')
        .select('id, name, city, region, country, latitude, longitude')
        .eq('is_active', true)
        .order('name');

      const { data, error } = await query;

      if (error) throw error;

      // Calculate distance if user location is available
      let branchesWithDistance = data || [];
      if (userLocation) {
        branchesWithDistance = data.map(branch => {
          if (branch.latitude && branch.longitude) {
            const distance = calculateDistance(
              userLocation.lat,
              userLocation.lng,
              parseFloat(branch.latitude),
              parseFloat(branch.longitude)
            );
            return { ...branch, distance_km: distance };
          }
          return branch;
        }).sort((a, b) => (a.distance_km || 999) - (b.distance_km || 999));

        // Auto-select nearest branch if none selected
        if (!selectedBranch && branchesWithDistance.length > 0) {
          setSelectedBranch(branchesWithDistance[0]);
        }
      }

      setBranches(branchesWithDistance);
    } catch (err) {
      console.error('Error fetching branches:', err);
    }
  };

  const fetchMenuItems = async () => {
    setLoading(true);
    setError(null);
    try {
      // Fetch categories
      const { data: catData, error: catError } = await supabase
        .from('website_menu_categories')
        .select('*')
        .eq('is_active', true)
        .order('sort_order');

      if (catError) throw catError;
      setCategories(catData || []);

      // Fetch menu items with branch-specific prices if branch selected
      let itemsQuery = supabase
        .from('website_menu_items')
        .select(`
          *,
          category:website_menu_categories(id, name_en, name_id, name_ar, name_ms)
        `)
        .eq('is_active', true)
        .order('sort_order');

      const { data: itemsData, error: itemsError } = await itemsQuery;

      if (itemsError) throw itemsError;

      // If a branch is selected, fetch branch-specific prices
      if (selectedBranch) {
        const { data: pricesData, error: pricesError } = await supabase
          .from('website_menu_item_prices')
          .select('*')
          .eq('branch_id', selectedBranch.id)
          .eq('is_available', true);

        if (!pricesError && pricesData) {
          // Merge prices with menu items
          const priceMap = new Map(pricesData.map(p => [p.menu_item_id, p]));
          const itemsWithPrices = itemsData.map(item => {
            const branchPrice = priceMap.get(item.id);
            if (branchPrice) {
              return {
                ...item,
                price: branchPrice.price,
                currency: branchPrice.currency,
                price_display: branchPrice.price_display || formatPrice(branchPrice.price, branchPrice.currency),
                is_available: branchPrice.is_available,
              };
            }
            return item;
          });
          setMenuItems(itemsWithPrices);
        } else {
          setMenuItems(itemsData || []);
        }
      } else {
        setMenuItems(itemsData || []);
      }
    } catch (err) {
      console.error('Error fetching menu:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  };

  // Filter menu items by category
  const filteredItems = useMemo(() => {
    if (selectedCategory === 'all') return menuItems;
    return menuItems.filter(item => item.category_id === selectedCategory);
  }, [menuItems, selectedCategory]);

  const showSkeleton = loading && menuItems.length === 0;
  const isRefetching = loading && menuItems.length > 0;

  return (
    <div className="min-h-screen bg-gray-50 py-12 md:py-16">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div ref={headerRef} className="text-center mb-8">
          <h1 className="section-title">{t('menu.title')}</h1>
          <p className="section-subtitle">{t('menu.subtitle')}</p>
        </div>

        {/* Branch Selector (for pricing) */}
        {branches.length > 0 && (
          <div ref={branchSelectorRef}>
            <BranchSelector
              branches={branches}
              selectedBranch={selectedBranch}
              onSelect={setSelectedBranch}
              userLocation={userLocation}
              t={t}
            />
          </div>
        )}

        {/* Category Filter */}
        <div ref={filterRef} className="flex flex-wrap justify-center gap-2 mb-8">
          <button
            onClick={() => setSelectedCategory('all')}
            className={`px-4 py-2 rounded-full transition-colors ${
              selectedCategory === 'all'
                ? 'bg-primary-600 text-white'
                : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
            }`}
          >
            {t('menu.allCategories')}
          </button>
          {categories.map((category) => (
            <button
              key={category.id}
              onClick={() => setSelectedCategory(category.id)}
              className={`px-4 py-2 rounded-full transition-colors ${
                selectedCategory === category.id
                  ? 'bg-primary-600 text-white'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              {getLocalizedField(category, 'name', lang)}
            </button>
          ))}
        </div>

        {/* Menu Items Grid */}
        <DataState
          loading={showSkeleton}
          error={error}
          empty={filteredItems.length === 0}
          onRetry={fetchMenuItems}
          skeleton={<SkeletonGrid count={8} className={MENU_GRID} />}
          emptyProps={{ icon: '🍽️', title: t('menu.noItems') }}
        >
          <div
            className={`${MENU_GRID} transition-opacity duration-300 ${isRefetching ? 'opacity-50 pointer-events-none' : ''}`}
            aria-busy={isRefetching}
          >
            {filteredItems.map((item, index) => (
              <MenuItemCard
                key={item.id}
                item={item}
                lang={lang}
                hasBranchSelected={!!selectedBranch}
                index={index}
              />
            ))}
          </div>
        </DataState>
      </div>
    </div>
  );
}
